import React from 'react';
import type { Page, Component } from '../ComponentEditModal.types';

interface HeadContentData {
  title?: string;
  description?: string;
  keywords?: string;
  canonicalUrl?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogImage?: string;
  rawHeadHtml?: string;
  inlineStyles?: string;
}

interface HeadContentEditorProps {
  formData: HeadContentData;
  setFormData: (data: HeadContentData) => void;
  pages: Page[];
  isLoadingPages: boolean;
  currentPageComponents?: Component[];
}

export const HeadContentEditor: React.FC<HeadContentEditorProps> = ({ formData, setFormData }) => {
  const data = formData;

  return (
    <div className="space-y-8">
      {/* SEO Section */}
      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-4">SEO Meta Tags</h3>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Page Title</label>
            <input
              type="text"
              value={data.title || ''}
              onChange={e => setFormData({ ...data, title: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
              placeholder="Page title for SEO"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Meta Description</label>
            <textarea
              value={data.description || ''}
              onChange={e => setFormData({ ...data, description: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
              rows={3}
              placeholder="Meta description for search engines"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Canonical URL</label>
            <input
              type="text"
              value={data.canonicalUrl || ''}
              onChange={e => setFormData({ ...data, canonicalUrl: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
              placeholder="https://example.com/page"
            />
            <p className="text-xs text-gray-500 mt-1">
              Leave blank to automatically use the page's URL
            </p>
          </div>
        </div>
      </div>

      {/* Open Graph Section */}
      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-4">Open Graph (Social Sharing)</h3>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">OG Title</label>
            <input
              type="text"
              value={data.ogTitle || ''}
              onChange={e => setFormData({ ...data, ogTitle: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
              placeholder="Title for social sharing"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">OG Description</label>
            <textarea
              value={data.ogDescription || ''}
              onChange={e => setFormData({ ...data, ogDescription: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
              rows={2}
              placeholder="Description for social sharing"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">OG Image URL</label>
            <input
              type="text"
              value={data.ogImage || ''}
              onChange={e => setFormData({ ...data, ogImage: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
              placeholder="https://example.com/image.jpg"
            />
          </div>
        </div>
      </div>

      {/* Custom Code Section */}
      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-4">Custom Code</h3>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Custom Head HTML</label>
            <textarea
              value={data.rawHeadHtml || ''}
              onChange={e => setFormData({ ...data, rawHeadHtml: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500 font-mono text-sm"
              rows={6}
              placeholder="<meta name='custom' content='value' />"
            />
            <p className="mt-1 text-xs text-gray-500">
              Add custom HTML to the &lt;head&gt; section. Use with caution.
            </p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Inline Styles (CSS)
            </label>
            <textarea
              value={data.inlineStyles || ''}
              onChange={e => setFormData({ ...data, inlineStyles: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500 font-mono text-sm"
              rows={4}
              placeholder=".custom-class { color: red; }"
            />
          </div>
        </div>
      </div>
    </div>
  );
};
